/**
 * OwnerManagementList Component
 *
 * Lists all owners of a gym with the ability to remove co-owners.
 * Includes confirmation dialog before removal and prevents removing
 * the current user or the last remaining owner.
 */

import { useState, type CSSProperties } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/lib/firebase';
import { useGymOwners } from '@/hooks/useGymOwners';
import { useAuth } from '@/hooks/useAuth';

/**
 * Props for OwnerManagementList component
 */
interface OwnerManagementListProps {
  /** ID of the gym to manage owners for */
  gymId: string;
}

/**
 * List component for managing gym owners
 */
export const OwnerManagementList = ({ gymId }: OwnerManagementListProps) => {
  const { user } = useAuth();
  const { owners, loading, error, ownerCount } = useGymOwners(gymId);

  const [removingOwnerId, setRemovingOwnerId] = useState<string | null>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  /**
   * Handle initiating owner removal (shows confirmation)
   */
  const handleRemoveClick = (ownerId: string) => {
    setConfirmingRemoval(ownerId);
    setRemoveError(null);
  };

  /**
   * Cancel the removal confirmation
   */
  const handleCancelRemoval = () => {
    setConfirmingRemoval(null);
    setRemoveError(null);
  };

  /**
   * Confirm and execute owner removal
   */
  const handleConfirmRemoval = async (ownerId: string) => {
    setRemovingOwnerId(ownerId);
    setRemoveError(null);

    try {
      const functions = getFirebaseFunctions();
      const removeOwner = httpsCallable<{ gymId: string; userId: string }, { success: boolean }>(
        functions,
        'removeOwner'
      );

      await removeOwner({ gymId, userId: ownerId });
      setConfirmingRemoval(null);
    } catch (err) {
      const error = err as { code?: string; message?: string };
      let errorMessage = 'Failed to remove owner. Please try again.';

      if (error.code === 'functions/failed-precondition') {
        errorMessage = 'Cannot remove the last owner of a gym.';
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to remove this owner.';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Owner not found.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setRemoveError(errorMessage);
    } finally {
      setRemovingOwnerId(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={loadingContainerStyle}>
        <div style={spinnerStyle} />
        <p style={loadingTextStyle}>Loading owners...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={errorContainerStyle}>
        <p style={errorTitleStyle}>Error loading owners</p>
        <p style={errorTextStyle}>{error}</p>
      </div>
    );
  }

  // Empty state
  if (owners.length === 0) {
    return (
      <div style={emptyStateStyle}>
        <p style={emptyStateTextStyle}>No owners found for this gym.</p>
      </div>
    );
  }

  const isLastOwner = ownerCount === 1;

  return (
    <div>
      {/* Warning about last owner */}
      {isLastOwner && (
        <div style={warningBannerStyle}>
          <span style={warningIconStyle}>!</span>
          <div>
            <p style={warningTitleStyle}>You are the only owner</p>
            <p style={warningTextStyle}>
              A gym must have at least one owner. Invite a co-owner before removing yourself.
            </p>
          </div>
        </div>
      )}

      {/* Remove error message */}
      {removeError && <div style={errorBannerStyle}>{removeError}</div>}

      {/* Owners list */}
      <div style={listContainerStyle}>
        {owners.map((owner) => {
          const isCurrentUser = owner.userId === user?.uid;
          const isConfirming = confirmingRemoval === owner.userId;
          const isRemoving = removingOwnerId === owner.userId;
          const canRemove = !isCurrentUser && !isLastOwner;

          return (
            <div key={owner.userId} style={ownerItemStyle}>
              {/* Owner photo */}
              {owner.photoURL ? (
                <img src={owner.photoURL} alt={owner.displayName} style={avatarImageStyle} />
              ) : (
                <div style={avatarPlaceholderStyle}>
                  {owner.displayName.charAt(0).toUpperCase()}
                </div>
              )}

              {/* Owner info */}
              <div style={ownerInfoStyle}>
                <div style={ownerNameRowStyle}>
                  <p style={ownerNameStyle}>{owner.displayName}</p>
                  {isCurrentUser && <span style={youBadgeStyle}>You</span>}
                </div>
                <p style={ownerEmailStyle}>{owner.email}</p>
              </div>

              {/* Remove button / confirmation */}
              {canRemove && (
                <div>
                  {isConfirming ? (
                    <div style={confirmActionsStyle}>
                      <button
                        onClick={() => handleConfirmRemoval(owner.userId)}
                        disabled={isRemoving}
                        style={{
                          ...confirmButtonStyle,
                          ...(isRemoving ? buttonDisabledStyle : {}),
                        }}
                      >
                        {isRemoving ? 'Removing...' : 'Confirm'}
                      </button>
                      <button
                        onClick={handleCancelRemoval}
                        disabled={isRemoving}
                        style={cancelButtonStyle}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleRemoveClick(owner.userId)}
                      style={removeButtonStyle}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Owner count */}
      <p style={ownerCountStyle}>
        {ownerCount} owner{ownerCount !== 1 ? 's' : ''} total
      </p>
    </div>
  );
};

/**
 * Component styles using CSS variables for dark mode support
 */
const loadingContainerStyle: CSSProperties = {
  padding: '24px',
  textAlign: 'center',
  color: 'var(--color-text-secondary)',
};

const spinnerStyle: CSSProperties = {
  display: 'inline-block',
  width: '24px',
  height: '24px',
  border: '2px solid var(--color-border)',
  borderTopColor: 'var(--color-primary)',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginBottom: '8px',
};

const loadingTextStyle: CSSProperties = {
  margin: 0,
  color: 'var(--color-text-secondary)',
};

const errorContainerStyle: CSSProperties = {
  padding: '16px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  borderRadius: '8px',
  border: '1px solid var(--color-error)',
};

const errorTitleStyle: CSSProperties = {
  fontWeight: 500,
  marginBottom: '4px',
  color: 'var(--color-error)',
};

const errorTextStyle: CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-error)',
  margin: 0,
};

const emptyStateStyle: CSSProperties = {
  padding: '24px',
  textAlign: 'center',
  color: 'var(--color-text-secondary)',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
};

const emptyStateTextStyle: CSSProperties = {
  margin: 0,
};

const warningBannerStyle: CSSProperties = {
  padding: '12px 16px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  borderRadius: '8px',
  border: '1px solid var(--color-warning)',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
};

const warningIconStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  backgroundColor: 'var(--color-warning)',
  borderRadius: '50%',
  color: 'white',
  fontSize: '12px',
  fontWeight: 700,
  flexShrink: 0,
};

const warningTitleStyle: CSSProperties = {
  fontWeight: 500,
  color: 'var(--color-badge-warning-text)',
  margin: 0,
  fontSize: '14px',
};

const warningTextStyle: CSSProperties = {
  color: 'var(--color-badge-warning-text)',
  margin: '4px 0 0 0',
  fontSize: '14px',
};

const errorBannerStyle: CSSProperties = {
  padding: '12px 16px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  borderRadius: '8px',
  border: '1px solid var(--color-error)',
  marginBottom: '16px',
  color: 'var(--color-error)',
  fontSize: '14px',
};

const listContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const ownerItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
};

const avatarImageStyle: CSSProperties = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  objectFit: 'cover',
};

const avatarPlaceholderStyle: CSSProperties = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-bg-tertiary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--color-text-secondary)',
  fontSize: '18px',
  fontWeight: 600,
};

const ownerInfoStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const ownerNameRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const ownerNameStyle: CSSProperties = {
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const youBadgeStyle: CSSProperties = {
  fontSize: '12px',
  padding: '2px 8px',
  backgroundColor: 'var(--color-badge-info-bg)',
  color: 'var(--color-primary)',
  borderRadius: '9999px',
};

const ownerEmailStyle: CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-text-secondary)',
  margin: '4px 0 0 0',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const confirmActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const confirmButtonStyle: CSSProperties = {
  padding: '8px 12px',
  backgroundColor: 'var(--color-error)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
};

const buttonDisabledStyle: CSSProperties = {
  opacity: 0.7,
  cursor: 'not-allowed',
};

const cancelButtonStyle: CSSProperties = {
  padding: '8px 12px',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
};

const removeButtonStyle: CSSProperties = {
  padding: '8px 12px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  color: 'var(--color-error)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
};

const ownerCountStyle: CSSProperties = {
  marginTop: '16px',
  fontSize: '14px',
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
};
